import { Component, inject, Injector, OnInit } from '@angular/core';
import { Database, get, ref } from '@angular/fire/database';
import { BaseComponent } from 'src/app/shared/baseComponent';

@Component({
  selector: 'app-exercise-analysis',
  templateUrl: './exercise-analysis.component.html',
  styleUrls: ['./exercise-analysis.component.css']
})
export class ExerciseAnalysisComponent extends BaseComponent implements OnInit {
  database = inject(Database)
  exerciseId: string = ''
  exercise: any = null
  players: any[] = []
  rawPlayerData: any = {}
  showRawData: boolean = false
  showPlayerModal: boolean = false
  selectedPlayer: any = {}
  totalComponentFileSize: number = 0
  totalCycleLoadTime: number = 0
  showComponentDownload: boolean = false
  showInteractivities: boolean = false
  ipLocation: any = null
  sortField: string = 'registeredAt'
  sortAsc: boolean = false

  sortBy(field: string) {
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc
    } else {
      this.sortField = field
      this.sortAsc = true
    }
    const stringFields = ['property', 'platform']
    this.players.sort((a: any, b: any) => {
      let valA = a[field] ?? null
      let valB = b[field] ?? null
      if (!stringFields.includes(field)) {
        valA = valA !== null ? Number(valA) : -Infinity
        valB = valB !== null ? Number(valB) : -Infinity
      }
      if (valA < valB) return this.sortAsc ? -1 : 1
      if (valA > valB) return this.sortAsc ? 1 : -1
      return 0
    })
  }

  constructor(injector: Injector) {
    super(injector)
  }

  async ngOnInit(): Promise<void> {
    this.exerciseId = this.route.snapshot.queryParams['exerciseId'] || ''
    if (this.exerciseId) {
      await this.loadExercise()
    }
  }

  async loadExercise() {
    this.loader.show()
    const refe = ref(this.database, 'performance/' + this.exerciseId)
    const snapshot = await get(refe)
    if (snapshot.exists()) {
      this.exercise = snapshot.val()
      this.exercise.property = this.exerciseId
      const tempArray = this.collectionToArrayFB(this.exercise).filter((p: any) => p.property !== 'property')
      this.rawPlayerData = {}
      this.players = []
      tempArray.forEach((player: any) => {
        this.rawPlayerData[player.property] = player
        this.players.push({
          property: player.property,
          registeredAt: player.registeredAt,
          userAgent: player.userAgent,
          platform: player.platform?.platform,
          mobile: player.platform?.mobile ?? false,
          browser: this.parseBrowser(player.platform?.brands),
          loadDuration: player.loadComponents?.duration_ms,
          renderDuration: player.render?.durationFor100CallsMs,
          fps: player.render?.fps,
          avgRenderTime: player.rendering?.averageRenderTime
        })
      })
      this.players.sort((a: any, b: any) => {
        return new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime()
      })
    }
    this.loader.hide()
  }

  parseBrowser(brands: string): string {
    if (!brands) return ''
    const known = ['Edge', 'Firefox', 'Chrome']
    for (const b of known) {
      if (brands.includes(b)) return b
    }
    return ''
  }

  async reloadPlayer() {
    if (this.selectedPlayer?.property) {
      await this.showPlayer(this.players.find((p: any) => p.property === this.selectedPlayer.property) || this.selectedPlayer)
    }
  }

  async showPlayer(player: any) {
    this.selectedPlayer = this.rawPlayerData[player.property] || player
    this.showPlayerModal = true
    this.showComponentDownload = false
    this.showInteractivities = false
    this.ipLocation = null
    if (this.selectedPlayer.ip) {
      this.ipLocation = await this.getIpLocation(this.selectedPlayer.ip)
    }
    // compute total component file size
    this.totalComponentFileSize = 0
    if (this.selectedPlayer.loadComponents?.formattedDetails) {
      this.selectedPlayer.loadComponents.formattedDetails.forEach((el: any) => {
        if (el.FileSizeKB) { this.totalComponentFileSize += el.FileSizeKB }
      })
    }
    // compute total cycle load time
    this.totalCycleLoadTime = 0
    if (this.selectedPlayer.render && this.selectedPlayer.babylonRender) {
      const fps = this.selectedPlayer.render.fps * 1.0
      const updateDuration = this.selectedPlayer.render.durationFor100CallsMs * 1.0
      const renderDuration = this.selectedPlayer.babylonRender.averageRenderTime * 1.0
      this.totalCycleLoadTime = (renderDuration + updateDuration / 100) * fps
    }
  }

  async getIpLocation(ip: string): Promise<any> {
    const response = await fetch(`https://ipapi.co/${ip}/json/`)
    return await response.json()
  }

  back() {
    this.router.navigate(['performance'])
  }

  goToFullDetails() {
    this.router.navigate(['performancedetail'], { queryParams: { exerciseid: this.exerciseId, playername: this.selectedPlayer.property } })
  }
}
